import { isGphEmployee } from '../automatic-distribution-employee';
import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../../../../../../environments/environment';
import { DistrictNames } from '../../../../../../sectors/sectors';
import {
  autoDistributionOptionsFromRecords,
  DEFAULT_AUTO_DISTRIBUTION_OPTIONS,
  SectorOption,
} from '../../../../../../sectors/sector-options';
import { SectorOptionsService } from '../../../../../../sectors/sector-options.service';
import { TicketsAssignService } from '../../tickets-assign.service';

// Пользователь может убрать блок районов командировочных из модалки — выбор запоминается в браузере
const TRIP_DISTRICTS_HIDDEN_KEY = 'automaticDistribution.tripDistrictsHidden';
// Стандартная смена командировочного: его график заведён в другом участке и приходит нулями
const TRIP_SHIFT_DEFAULT_BEGIN = '09:00';
const TRIP_SHIFT_DEFAULT_END = '18:00';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-automatic-distribution-settings-modal-new',
  templateUrl: './automatic-distribution-settings-modal.component.html',
  styleUrls: ['./automatic-distribution-settings-modal.component.scss'],
  standalone: false,
})
export class AutomaticDistributionSettingsModalComponent implements OnInit, OnDestroy {
  advancedSettings = true;
  alreadyDistributedToday = false;
  allowUnassigned = false;
  distributionDate = '';
  districts: DistrictNames[] = [];
  employees: any[] = [];
  employeesError = false;
  employeesLoading = false;
  gpsType: 1 | 2 | null = 2;
  readonly showAssignMode = !environment.production;
  readonly publicTransportEnabled = !environment.production;
  launchMode: 'assign' | 'preview' = 'preview';
  scenario: 'distribution' | 'analysis' = 'distribution';
  sectorId: number | null = null;
  distributionStrategy: string = DEFAULT_AUTO_DISTRIBUTION_OPTIONS.distributionStrategy;
  limitationType: 1 | 2 | 3 = DEFAULT_AUTO_DISTRIBUTION_OPTIONS.limitationType;
  // Сохранённые настройки участка (sector_option): подставляются при открытии модалки
  sectorOptionsLoading = false;
  sectorOptionsApplied = false;
  step: 'distributionType' | 'gpsType' = 'distributionType';
  ticketStatuses: any[] = [];
  ticketStatusesSelected: any[] = [];
  tripDistrictsHidden = false;
  public moment = require('moment');
  private employeesSubscription: Subscription;
  private sectorOptionsSubscription: Subscription;

  constructor(
    public activeModal: NgbActiveModal,
    private ticketsAssignService: TicketsAssignService,
    private sectorOptionsService: SectorOptionsService
  ) {
    this.tripDistrictsHidden = this.readTripDistrictsHidden();
  }

  ngOnInit(): void {
    this.applyDefaultLaunchMode();
    this.loadSectorOptions();
  }

  /** «Распределить и Сохранить» доступно не на проде и если по участку сегодня ещё не распределяли */
  get isAssignModeAvailable(): boolean {
    return this.showAssignMode && !this.alreadyDistributedToday;
  }

  /** По умолчанию «Распределить и Сохранить», «Предпросмотр» остаётся только когда первый недоступен */
  private applyDefaultLaunchMode(): void {
    this.launchMode = this.isAssignModeAvailable ? 'assign' : 'preview';
  }

  ngOnDestroy(): void {
    if (this.employeesSubscription) {
      this.employeesSubscription.unsubscribe();
    }
    if (this.sectorOptionsSubscription) {
      this.sectorOptionsSubscription.unsubscribe();
    }
  }

  /** Подставляет сохранённые на участке настройки; без них остаются значения по умолчанию */
  private loadSectorOptions(): void {
    if (!this.sectorId) {
      return;
    }

    this.sectorOptionsLoading = true;
    this.sectorOptionsSubscription = this.sectorOptionsService.getAutoDistributionOptions(this.sectorId).subscribe(
      (records: SectorOption[]) => {
        this.sectorOptionsLoading = false;

        if (!records || !records.length) {
          return;
        }

        const options = autoDistributionOptionsFromRecords(records);

        // Тип распределения (allow_unassigned) в настройках участка не хранится — остаётся выбор в модалке.
        // Тип отображения не берём из настроек: по умолчанию всегда «Распределить и Сохранить»
        this.distributionStrategy = options.distributionStrategy;
        this.limitationType = options.limitationType;
        this.sectorOptionsApplied = true;
      },
      () => {
        // Модалка должна работать и без сохранённых настроек
        this.sectorOptionsLoading = false;
      }
    );
  }

  nextStep(): void {
    if (this.scenario === 'distribution' && !this.isDistributionAvailable) {
      return;
    }

    if (
      (!this.showAssignMode || this.alreadyDistributedToday) &&
      this.launchMode === 'assign'
    ) {
      this.launchMode = 'preview';
    }

    this.step = 'gpsType';
    this.loadEmployees();
  }

  startAnalysis(): void {
    if (!this.isAnalysisAvailable) {
      return;
    }

    this.scenario = 'analysis';
    this.launchMode = 'preview';
    this.nextStep();
  }

  previousStep(): void {
    this.scenario = 'distribution';
    this.step = 'distributionType';
  }

  get isAnalysisAvailable(): boolean {
    const selectedDate = this.moment(this.distributionDate, 'YYYY-MM-DD', true);

    if (!this.distributionDate || !selectedDate.isValid()) {
      return false;
    }

    return selectedDate.isSameOrBefore(this.moment(), 'day');
  }

  get isDistributionAvailable(): boolean {
    const selectedDate = this.moment(this.distributionDate, 'YYYY-MM-DD', true);

    if (!this.distributionDate || !selectedDate.isValid()) {
      return false;
    }

    return selectedDate.isSameOrAfter(this.moment(), 'day');
  }

  setTicketStatuses(statuses): void {
    this.ticketStatusesSelected = statuses || [];
  }

  setGpsType(gpsType: 1 | 2 | null): void {
    if (gpsType === null) {
      return;
    }

    if (gpsType === 1 && !this.hasEmployeeWithGpsCoordinate) {
      return;
    }

    this.gpsType = gpsType;

    this.employees.forEach((employee) => {
      employee.coordinate_type = gpsType === 1 && this.hasEmployeeGpsCoordinate(employee) ? 2 : 1;
    });
  }

  setEmployeeCoordinateType(employee, coordinateType: 1 | 2 | 3): void {
    if (this.isEmployeeCoordinateTypeDisabled(employee, coordinateType)) {
      return;
    }

    employee.coordinate_type = coordinateType;
    this.syncGpsTypeFromEmployees();
  }

  setEmployeeMovementType(employee, movementType: 1 | 2 | 3): void {
    if (movementType === 3 && !this.publicTransportEnabled) {
      return;
    }

    employee.movement_type = movementType;
  }

  get hasEmployeeWithGpsCoordinate(): boolean {
    return this.employees.some((employee) => this.hasEmployeeGpsCoordinate(employee));
  }

  isEmployeeCoordinateTypeDisabled(employee, coordinateType: 1 | 2 | 3): boolean {
    if (coordinateType === 2) {
      return !this.hasEmployeeGpsCoordinate(employee);
    }

    if (coordinateType === 3) {
      return !this.hasEmployeeHomeCoordinate(employee);
    }

    return false;
  }

  hasEmployeeGpsCoordinate(employee): boolean {
    return this.getEmployeeMapUrl(employee) !== null;
  }

  hasEmployeeHomeCoordinate(employee): boolean {
    return this.getEmployeeHomeMapUrl(employee) !== null;
  }

  private normalizeMovementType(movementType): 1 | 2 | 3 {
    const value = Number(movementType);

    if ([1, 2, 3].indexOf(value) === -1) {
      return 1;
    }

    if (value === 3 && !this.publicTransportEnabled) {
      return 1;
    }

    return value as 1 | 2 | 3;
  }

  getEmployeeName(employee): string {
    return [employee && employee.last_name, employee && employee.first_name, employee && employee.patronymic_name]
      .filter((name) => !!name)
      .join(' ');
  }

  public isEmployeeGph = isGphEmployee;

  isEmployeeOnTrip(employee): boolean {
    return !!(employee && employee.is_trip);
  }

  get tripEmployees(): any[] {
    return this.employees.filter((employee) => this.isEmployeeOnTrip(employee));
  }

  // Районы имеют смысл только при гибкой или жёсткой стратегии распределения по районам
  get isDistrictLimitationEnabled(): boolean {
    return this.limitationType !== 1;
  }

  // Блок районов показываем, когда стратегия по районам включена, на смене есть командировочные и районы загрузились
  get hasTripDistrictsSettings(): boolean {
    return this.isDistrictLimitationEnabled && !!this.districts.length && this.tripEmployees.length > 0;
  }

  setEmployeeDistricts(employee, districts): void {
    employee.districts = districts || [];
  }

  toggleTripDistrictsHidden(): void {
    this.tripDistrictsHidden = !this.tripDistrictsHidden;

    try {
      localStorage.setItem(TRIP_DISTRICTS_HIDDEN_KEY, this.tripDistrictsHidden ? '1' : '0');
    } catch (e) {}
  }

  private readTripDistrictsHidden(): boolean {
    try {
      return localStorage.getItem(TRIP_DISTRICTS_HIDDEN_KEY) === '1';
    } catch (e) {
      return false;
    }
  }

  /**
   * Командировочный не закреплён за районами этого участка, поэтому district_ids
   * уходят только у него — ровно то, что выбрано; сняли все — уйдёт пустой список
   */
  private withDistrictIds(employee) {
    if (!this.isDistrictLimitationEnabled || !this.isEmployeeOnTrip(employee) || !this.districts.length) {
      return employee;
    }

    const selected = (employee && employee.districts) || [];

    return Object.assign({}, employee, {
      district_ids: selected.map((district) => Number(district && district.d_id)).filter((id) => Number.isFinite(id)),
    });
  }

  /**
   * У командировочного сотрудника график заведён в другом участке, поэтому
   * int_begin/int_end приходят нулями. По условию «есть начало и конец смены»
   * такие сотрудники выпадали из таблицы целиком, хотя на смене они есть
   */
  private isEmployeeOnShift(employee): boolean {
    if (!employee) {
      return false;
    }

    return this.isEmployeeOnTrip(employee) || !!(employee.int_begin && employee.int_end);
  }

  /**
   * У командировочного сотрудника время смены правится отдельным блоком,
   * поэтому в мета-строке остаётся только статус смены
   */
  getEmployeeMeta(employee): string {
    const parts: string[] = [];

    if (!this.isEmployeeOnTrip(employee)) {
      parts.push(this.getEmployeeWorkTime(employee));
    }
    if (employee && employee.sws_name) {
      parts.push(employee.sws_name);
    }

    return parts.join(' · ');
  }

  commitEmployeeShiftBegin(employee): void {
    employee.shift_begin = this.normalizeTimeText(employee && employee.shift_begin);
    employee.int_begin = employee.shift_begin || 0;
  }

  commitEmployeeShiftEnd(employee): void {
    employee.shift_end = this.normalizeTimeText(employee && employee.shift_end);
    employee.int_end = employee.shift_end || 0;
  }

  /**
   * int_begin/int_end приходят строкой «09:00», а у командировочных нулём —
   * в поле показываем только разбираемое время, иначе оставляем пустым
   */
  private toTimeText(value): string {
    return this.normalizeTimeText(value);
  }

  private normalizeTimeText(value): string {
    const digits = String(value === null || value === undefined ? '' : value).replace(/[^0-9]/g, '');

    if (digits.length < 4) {
      return '';
    }

    const hour = Math.min(Number(digits.slice(0, 2)), 23);
    const minute = Math.min(Number(digits.slice(2, 4)), 59);

    return `${this.padTimePart(hour)}:${this.padTimePart(minute)}`;
  }

  private padTimePart(value): string {
    const numberValue = Number(value) || 0;

    return `${numberValue < 10 ? '0' : ''}${numberValue}`;
  }

  getEmployeeWorkTime(employee): string {
    const begin = employee && employee.int_begin;
    const end = employee && employee.int_end;

    return begin || end ? `${begin || '--:--'}–${end || '--:--'}` : 'Время смены не указано';
  }

  getEmployeeMapUrl(employee): string | null {
    return this.getMapUrl(employee && employee.last_coordinate);
  }

  getEmployeeHomeMapUrl(employee): string | null {
    return this.getMapUrl(employee && employee.home_coordinate);
  }

  private getMapUrl(coordinate): string | null {
    if (
      !coordinate ||
      coordinate.latitude === null ||
      coordinate.latitude === undefined ||
      coordinate.latitude === '' ||
      coordinate.longitude === null ||
      coordinate.longitude === undefined ||
      coordinate.longitude === ''
    ) {
      return null;
    }

    const latitude = Number(coordinate.latitude);
    const longitude = Number(coordinate.longitude);

    if (!Number.isFinite(latitude) || !Number.isFinite(longitude) || latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
      return null;
    }

    return `https://www.openstreetmap.org/?mlat=${latitude}&mlon=${longitude}#map=18/${latitude}/${longitude}`;
  }

  getDistributionDateLabel(): string {
    const parts = (this.distributionDate || '').split('-');

    return parts.length === 3 ? `${parts[2]}.${parts[1]}.${parts[0]}` : this.distributionDate;
  }

  trackByEmployeeId(index: number, employee): number {
    return employee && employee.user_id;
  }

  submit(): void {
    if (this.employeesLoading || this.employeesError) {
      return;
    }

    if (
      (!this.showAssignMode || this.alreadyDistributedToday) &&
      this.launchMode === 'assign'
    ) {
      this.launchMode = 'preview';
    }

    const settings = {
      allow_unassigned: this.allowUnassigned,
      launch_mode: this.scenario === 'analysis' ? 'preview' : this.launchMode,
      assign_after_result: this.scenario === 'analysis' ? false : this.launchMode === 'assign',
      distribution_strategy: this.distributionStrategy,
      limitation_type: this.limitationType,
      employees: this.employees.map((employee) => this.withDistrictIds(employee)),
      scenario: this.scenario,
    };

    if (this.scenario === 'analysis' && this.ticketStatusesSelected.length) {
      Object.assign(settings, {
        ticket_status: this.ticketStatusesSelected.map((status) => Number(status && status.id)).filter((status) => Number.isFinite(status)),
      });
    }

    this.activeModal.close(settings);
  }

  loadEmployees(): void {
    if (!this.sectorId || !this.distributionDate) {
      this.employees = [];
      this.employeesError = true;
      return;
    }

    if (this.employeesSubscription) {
      this.employeesSubscription.unsubscribe();
    }

    this.employeesLoading = true;
    this.employeesError = false;
    const employeesRequest = this.ticketsAssignService.getAutomaticDistributionEmployees(
      this.sectorId,
      this.distributionDate,
      this.limitationType
    );
    // Районы нужны только для командировочных при включённой стратегии по районам;
    // без них модалка всё равно должна открыться
    const districtsRequest: Observable<any> = this.isDistrictLimitationEnabled
      ? this.ticketsAssignService.getDistricts({ sectors_ids: [this.sectorId] }).pipe(catchError(() => of({ data: [] })))
      : of({ data: [] });
    const request: Observable<any> = forkJoin(
      this.scenario === 'analysis'
        ? [employeesRequest, districtsRequest, this.ticketsAssignService.getTicketsStatuses()]
        : [employeesRequest, districtsRequest]
    );

    this.employeesSubscription = request.subscribe(
      (response) => {
        const employeesResponse = response[0];
        const employees = (employeesResponse && employeesResponse.data && employeesResponse.data.employees) || [];

        this.districts = (response[1] && response[1].data) || [];
        this.employees = employees
          .filter((employee) => this.isEmployeeOnShift(employee))
          .map((employee) => {
            const isTrip = this.isEmployeeOnTrip(employee);
            // Командировочному без времени смены подставляем стандартную 09:00–18:00, диспетчер может поправить
            const shiftBegin = this.toTimeText(employee && employee.int_begin) || (isTrip ? TRIP_SHIFT_DEFAULT_BEGIN : '');
            const shiftEnd = this.toTimeText(employee && employee.int_end) || (isTrip ? TRIP_SHIFT_DEFAULT_END : '');

            return Object.assign({}, employee, {
              coordinate_type: [1, 2, 3].indexOf(Number(employee && employee.coordinate_type)) > -1
                ? Number(employee.coordinate_type)
                : 1,
              movement_type: this.normalizeMovementType(employee && employee.movement_type),
              shift_begin: shiftBegin,
              shift_end: shiftEnd,
              // У командировочного в запрос уходит то, что в полях смены (как и после ручного ввода)
              int_begin: isTrip ? shiftBegin || 0 : employee.int_begin,
              int_end: isTrip ? shiftEnd || 0 : employee.int_end,
              // По умолчанию командировочному доступны все районы участка
              districts: isTrip ? this.districts.slice() : [],
            });
          });

        if (this.scenario === 'analysis') {
          const statusesResponse = response[2];
          const statuses = (statusesResponse && statusesResponse.data) || [];

          this.ticketStatuses = statuses;
          this.ticketStatusesSelected = this.ticketStatuses.filter((status) => {
            const name = String((status && status.name) || '').toLowerCase();

            return name.indexOf('выполн') > -1 || name.indexOf('заверш') > -1;
          });
        }

        this.syncGpsTypeFromEmployees();
        this.employeesLoading = false;
      },
      () => {
        this.employees = [];
        this.districts = [];
        this.employeesLoading = false;
        this.employeesError = true;
      }
    );
  }

  private syncGpsTypeFromEmployees(): void {
    if (!this.employees.length) {
      return;
    }

    const allEmployeesUseOffice = this.employees.every((employee) => employee.coordinate_type === 1);

    if (allEmployeesUseOffice) {
      this.gpsType = 2;

      return;
    }

    const allEmployeesUseGpsWhereAvailable = this.employees.every((employee) =>
      this.hasEmployeeGpsCoordinate(employee) ? employee.coordinate_type === 2 : employee.coordinate_type === 1
    );

    this.gpsType = allEmployeesUseGpsWhereAvailable ? 1 : null;
  }
}
